import React, { useState, useEffect } from 'react';
import { MessageCircle, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// Olion sales WhatsApp number — single source of truth.
const WHATSAPP_NUMBER = '21695806095'; // E.164, no '+' for wa.me

const PREFILL_MESSAGES = {
  fr: "Bonjour Olion, je viens de visiter votre site et j'aimerais avoir plus d'informations.",
  en: 'Hello Olion, I just visited your website and would like more information.',
  ar: 'مرحبا أوليون، لقد زرت موقعكم وأود الحصول على مزيد من المعلومات.',
};

const TOOLTIP = {
  fr: { title: 'Discutons sur WhatsApp', sub: "Réponse rapide pendant les heures d'ouverture", cta: 'Démarrer la conversation' },
  en: { title: "Let's chat on WhatsApp", sub: 'Fast reply during business hours', cta: 'Start the conversation' },
  ar: { title: 'دعنا نتحدث على واتساب', sub: 'رد سريع خلال ساعات العمل', cta: 'ابدأ المحادثة' },
};

export const WhatsAppFloat = () => {
  const { language, direction } = useLanguage();
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  const t = TOOLTIP[language] || TOOLTIP.fr;
  const prefill = encodeURIComponent(PREFILL_MESSAGES[language] || PREFILL_MESSAGES.fr);
  const href = `https://wa.me/${WHATSAPP_NUMBER}?text=${prefill}`;

  // Auto-pop the tooltip once, 4 seconds after first mount, to draw attention.
  useEffect(() => {
    const t1 = setTimeout(() => setMounted(true), 200);
    const t2 = setTimeout(() => setOpen(true), 4000);
    const t3 = setTimeout(() => setOpen(false), 9000);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);

  const sideClass = direction === 'rtl' ? 'left-5 sm:left-7' : 'right-5 sm:right-7';
  const tooltipAlign = direction === 'rtl' ? 'left-0' : 'right-0';
  const arrowSide = direction === 'rtl' ? 'left-6' : 'right-6';

  return (
    <div
      className={`fixed bottom-5 sm:bottom-7 ${sideClass} z-40 flex flex-col items-end gap-3 ${
        mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      } transition-all duration-500`}
      data-testid="whatsapp-float"
    >
      {/* Tooltip card */}
      {open && (
        <div
          className={`relative ${tooltipAlign} w-72 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-300`}
        >
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="absolute top-2 right-2 w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-700 transition-colors z-10"
            aria-label="Close"
            data-testid="whatsapp-tooltip-close"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Header strip */}
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-4 py-3 flex items-center gap-3">
            <div className="relative">
              <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-white" fill="white" />
              </div>
              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-green-400 border-2 border-white" />
            </div>
            <div className={`flex-1 text-white ${direction === 'rtl' ? 'text-right' : ''}`}>
              <div className="font-bold text-sm leading-tight">Olion</div>
              <div className="text-[11px] opacity-90">
                {language === 'ar' ? 'متصل الآن' : language === 'en' ? 'Online now' : 'En ligne maintenant'}
              </div>
            </div>
          </div>

          {/* Body */}
          <div className={`p-4 ${direction === 'rtl' ? 'text-right' : ''}`}>
            <p className="text-sm font-semibold text-gray-900 mb-1 leading-snug">{t.title}</p>
            <p className="text-xs text-gray-500 mb-3 leading-relaxed">{t.sub}</p>
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-tooltip-cta"
              className="block w-full text-center bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white font-bold text-sm py-2.5 rounded-xl transition-all shadow-md hover:shadow-lg active:scale-[0.98]"
            >
              {t.cta}
            </a>
          </div>

          {/* Arrow pointing to FAB */}
          <div className={`absolute -bottom-2 ${arrowSide} w-4 h-4 bg-white rotate-45 border-r border-b border-gray-100`} />
        </div>
      )}

      {/* Floating action button */}
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="WhatsApp Olion"
        data-testid="whatsapp-fab"
        onMouseEnter={() => setOpen(true)}
        className="relative group w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 shadow-2xl flex items-center justify-center text-white hover:scale-110 transition-transform duration-300"
      >
        {/* Pulse ring */}
        <span className="absolute inset-0 rounded-full bg-green-500 opacity-50 animate-ping" />
        <span className="relative">
          <MessageCircle className="w-7 h-7 sm:w-8 sm:h-8" fill="white" />
        </span>
        {/* Online dot */}
        <span className="absolute top-1 right-1 w-3 h-3 rounded-full bg-green-400 border-2 border-white" />
      </a>
    </div>
  );
};
